/**
 * MEVIA - Personality Tab (Enneagram)
 * Shows pure Enneagram dashboard and reports only (no HR indicators)
 */

import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { 
  Users, 
  Clock, 
  ArrowLeft,
  BarChart3,
  TrendingUp,
  Info,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Loader2,
  Eye,
  Download,
  Target,
  Shield,
  Zap,
  Heart
} from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import logger from '../../utils/logger';

// Enneagram type configurations with brief descriptions
const ENNEAGRAM_TYPES = {
  1: { 
    name: { es: 'El Perfeccionista', en: 'The Perfectionist' }, 
    icon: '🎯', 
    color: '#6366F1',
    brief: { es: 'Ético, organizado, busca mejorar todo', en: 'Ethical, organized, seeks to improve everything' }
  },
  2: { 
    name: { es: 'El Ayudador', en: 'The Helper' }, 
    icon: '❤️', 
    color: '#EC4899',
    brief: { es: 'Generoso, empático, orientado a relaciones', en: 'Generous, empathetic, relationship-oriented' }
  },
  3: { 
    name: { es: 'El Triunfador', en: 'The Achiever' }, 
    icon: '🏆', 
    color: '#F59E0B',
    brief: { es: 'Ambicioso, eficiente, orientado al éxito', en: 'Ambitious, efficient, success-oriented' }
  },
  4: { 
    name: { es: 'El Individualista', en: 'The Individualist' }, 
    icon: '🎨', 
    color: '#8B5CF6',
    brief: { es: 'Creativo, sensible, busca autenticidad', en: 'Creative, sensitive, seeks authenticity' }
  },
  5: { 
    name: { es: 'El Investigador', en: 'The Investigator' }, 
    icon: '🔬', 
    color: '#06B6D4',
    brief: { es: 'Analítico, reservado, acumula conocimiento', en: 'Analytical, reserved, accumulates knowledge' }
  },
  6: { 
    name: { es: 'El Leal', en: 'The Loyalist' }, 
    icon: '🛡️', 
    color: '#78716C',
    brief: { es: 'Responsable, comprometido, busca seguridad', en: 'Responsible, committed, seeks security' }
  },
  7: { 
    name: { es: 'El Entusiasta', en: 'The Enthusiast' }, 
    icon: '🎉', 
    color: '#22C55E',
    brief: { es: 'Optimista, versátil, busca experiencias', en: 'Optimistic, versatile, seeks experiences' }
  },
  8: { 
    name: { es: 'El Desafiador', en: 'The Challenger' }, 
    icon: '⚡', 
    color: '#EF4444',
    brief: { es: 'Decidido, protector, busca control', en: 'Decisive, protective, seeks control' }
  },
  9: { 
    name: { es: 'El Pacificador', en: 'The Peacemaker' }, 
    icon: '☮️', 
    color: '#64748B',
    brief: { es: 'Tranquilo, mediador, busca armonía', en: 'Calm, mediator, seeks harmony' }
  }
};

const PersonalityTab = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReports, setShowReports] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeReport, setEmployeeReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [showLegend, setShowLegend] = useState(false);
  const reportRef = useRef(null);
  
  // Employee search states
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);

  useEffect(() => {
    loadData();
  }, [language]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, employeesRes] = await Promise.all([
        axios.get(`/enneagram/company/stats?language=${language}`),
        axios.get(`/enneagram/company/employees?language=${language}`)
      ]);
      
      if (statsRes.data.success) {
        setStats(statsRes.data.data);
      }
      if (employeesRes.data.success) {
        setEmployees(employeesRes.data.data.employees || []);
      }
    } catch (error) {
      logger.error('Error loading data:', error);
      toast.error(language === 'es' ? 'Error al cargar datos' : 'Error loading data');
    } finally {
      setLoading(false);
    }
  };

  const loadEmployeeReport = async (employee) => {
    setSelectedEmployee(employee);
    setLoadingReport(true);
    try {
      // Get Enneagram assessments
      const assessmentsRes = await axios.get(`/enneagram/assessments?user_id=${employee.id}`);

      if (assessmentsRes.data.success) {
        const assessments = assessmentsRes.data.data.assessments || [];
        const completed = assessments.find(a => a.status === 'completado');
        
        if (completed) {
          const resultsRes = await axios.get(`/enneagram/assessments/${completed.id}/results?language=${language}`);
          if (resultsRes.data.success) {
            const data = resultsRes.data.data;
            // Transform data to expected format with all details (pure Enneagram data only)
            const transformedReport = {
              ...data,
              primary_type: {
                number: data.primary_type?.number,
                percentage: data.primary_type?.percentage,
                name: data.primary_type?.details?.name,
                icon: data.primary_type?.details?.icon,
                color: data.primary_type?.details?.color,
                description: data.primary_type?.details?.short_description,
                core_motivation: data.primary_type?.details?.core_motivation,
                core_fear: data.primary_type?.details?.core_fear,
                strengths: data.primary_type?.details?.strengths || [],
                challenges: data.primary_type?.details?.challenges || [],
                growth_path: data.primary_type?.details?.growth_path,
                stress_behavior: data.primary_type?.details?.stress_behavior,
                security_behavior: data.primary_type?.details?.security_behavior
              },
              wing_type: data.wing_type?.details ? {
                number: data.wing_type?.number,
                percentage: data.wing_type?.percentage,
                name: data.wing_type?.details?.name,
                icon: data.wing_type?.details?.icon,
                color: data.wing_type?.details?.color,
                description: data.wing_type?.details?.short_description,
                strengths: data.wing_type?.details?.strengths || [],
                challenges: data.wing_type?.details?.challenges || []
              } : null,
              all_percentages: data.all_percentages 
                ? Object.entries(data.all_percentages).map(([type, pct]) => ({
                    type: parseInt(type),
                    percentage: pct
                  })).sort((a, b) => b.percentage - a.percentage)
                : []
            };
            setEmployeeReport(transformedReport);
          }
        }
      }
    } catch (error) {
      logger.error('Error loading report:', error);
      toast.error(language === 'es' ? 'Error al cargar reporte' : 'Error loading report');
    } finally {
      setLoadingReport(false);
    }
  };

  const exportToPDF = async () => {
    const reportContent = reportRef.current;
    if (!reportContent) {
      toast.error(language === 'es' ? 'Error al generar PDF' : 'Error generating PDF');
      return;
    }
    
    // Show loading toast
    const loadingToast = toast.loading(
      language === 'es' ? 'Generando PDF...' : 'Generating PDF...'
    );
    
    try {
      // Dynamic imports for PDF generation
      const html2canvas = (await import('html2canvas')).default;
      const jsPDF = (await import('jspdf')).default;
      
      // Capture the report content as canvas
      const canvas = await html2canvas(reportContent, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      
      const imgData = canvas.toDataURL('image/png');
      
      // Calculate PDF dimensions (A4)
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      
      // Calculate the ratio to fit the image to PDF width
      const ratio = pdfWidth / imgWidth;
      const scaledHeight = imgHeight * ratio;
      
      // Add pages if content is longer than one page
      let heightLeft = scaledHeight;
      let position = 0;
      
      // Add first page
      pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, scaledHeight);
      heightLeft -= pdfHeight;
      
      // Add additional pages if needed
      while (heightLeft > 0) {
        position = heightLeft - scaledHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, scaledHeight);
        heightLeft -= pdfHeight;
      }
      
      // Generate filename
      const fileName = `Reporte_Eneagrama_${selectedEmployee?.nombre?.replace(/\s+/g, '_') || 'Empleado'}_${new Date().toISOString().split('T')[0]}.pdf`;
      
      // Download the PDF
      pdf.save(fileName);
      
      toast.dismiss(loadingToast);
      toast.success(language === 'es' ? 'PDF descargado exitosamente' : 'PDF downloaded successfully');
    } catch (error) {
      logger.error('Error generating PDF:', error);
      toast.dismiss(loadingToast);
      toast.error(language === 'es' ? 'Error al generar el PDF' : 'Error generating PDF');
    }
  };

  // Individual Employee Report View - EXPANDED
  if (showReports && selectedEmployee && employeeReport) {
    return (
      <div className="space-y-6">
        {/* Header with Back and Export buttons */}
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => { setSelectedEmployee(null); setEmployeeReport(null); }}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {language === 'es' ? 'Regresar' : 'Back'}
          </Button>
          <Button onClick={exportToPDF} className="bg-purple-600 hover:bg-purple-700">
            <Download className="h-4 w-4 mr-2" />
            {language === 'es' ? 'Exportar PDF' : 'Export PDF'}
          </Button>
        </div>

        <div ref={reportRef}>
          {/* Report Header */}
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold">
              🎭 {language === 'es' ? 'Reporte de Eneagrama' : 'Enneagram Report'}
            </h2>
            <p className="text-lg text-gray-600">{selectedEmployee.nombre}</p>
            <p className="text-sm text-gray-500">
              {selectedEmployee.email} {selectedEmployee.departamento && `| ${selectedEmployee.departamento}`}
            </p>
          </div>

          {/* Primary Type - Main Card */}
          <Card className="border-l-4 mb-6" style={{ borderLeftColor: employeeReport.primary_type?.color }}>
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <span className="text-4xl">{employeeReport.primary_type?.icon}</span>
                <div>
                  <span className="text-2xl">Tipo {employeeReport.primary_type?.number}: </span>
                  <span className="text-2xl" style={{ color: employeeReport.primary_type?.color }}>
                    {employeeReport.primary_type?.name}
                  </span>
                </div>
              </CardTitle>
              <CardDescription className="text-base">
                {language === 'es' ? 'Tipo Principal' : 'Primary Type'} - {employeeReport.primary_type?.percentage}% {language === 'es' ? 'de afinidad' : 'affinity'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Description */}
              <div>
                <h4 className="font-semibold mb-2">{language === 'es' ? '📝 Descripción' : '📝 Description'}</h4>
                <p className="text-gray-700 leading-relaxed">{employeeReport.primary_type?.description}</p>
              </div>
              
              {/* Core Motivation & Fear */}
              <div className="grid md:grid-cols-2 gap-4">
                {employeeReport.primary_type?.core_motivation && (
                  <div className="p-3 bg-green-50 rounded-lg">
                    <p className="font-medium text-green-700 flex items-center gap-2">
                      <Heart className="h-4 w-4" />
                      {language === 'es' ? 'Motivación Central' : 'Core Motivation'}
                    </p>
                    <p className="text-sm text-green-600 mt-1">{employeeReport.primary_type.core_motivation}</p>
                  </div>
                )}
                {employeeReport.primary_type?.core_fear && (
                  <div className="p-3 bg-red-50 rounded-lg">
                    <p className="font-medium text-red-700 flex items-center gap-2">
                      <Shield className="h-4 w-4" />
                      {language === 'es' ? 'Miedo Central' : 'Core Fear'}
                    </p>
                    <p className="text-sm text-red-600 mt-1">{employeeReport.primary_type.core_fear}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Strengths & Challenges - Full Cards */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <Card className="border-l-4 border-l-green-500">
              <CardHeader className="pb-2">
                <CardTitle className="text-green-700 flex items-center gap-2">
                  💪 {language === 'es' ? 'Fortalezas' : 'Strengths'}
                </CardTitle>
                <CardDescription>
                  {language === 'es' ? 'Capacidades naturales del Tipo' : 'Natural capabilities of this Type'} {employeeReport.primary_type?.number}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {employeeReport.primary_type?.strengths?.map((s, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">✓</span>
                      <span className="text-gray-700">{s}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-amber-500">
              <CardHeader className="pb-2">
                <CardTitle className="text-amber-700 flex items-center gap-2">
                  🎯 {language === 'es' ? 'Áreas de Desarrollo' : 'Growth Areas'}
                </CardTitle>
                <CardDescription>
                  {language === 'es' ? 'Desafíos comunes a trabajar' : 'Common challenges to work on'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {employeeReport.primary_type?.challenges?.map((c, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-amber-500 mt-1">•</span>
                      <span className="text-gray-700">{c}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Behaviors Under Stress and Security */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {employeeReport.primary_type?.stress_behavior && (
              <Card className="border-l-4 border-l-red-400">
                <CardHeader className="pb-2">
                  <CardTitle className="text-red-600 text-base flex items-center gap-2">
                    <Zap className="h-4 w-4" />
                    {language === 'es' ? 'Bajo Estrés' : 'Under Stress'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 text-sm">{employeeReport.primary_type.stress_behavior}</p>
                </CardContent>
              </Card>
            )}
            {employeeReport.primary_type?.security_behavior && (
              <Card className="border-l-4 border-l-blue-400">
                <CardHeader className="pb-2">
                  <CardTitle className="text-blue-600 text-base flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    {language === 'es' ? 'En Seguridad' : 'In Security'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 text-sm">{employeeReport.primary_type.security_behavior}</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Growth Path */}
          {employeeReport.primary_type?.growth_path && (
            <Card className="border-l-4 border-l-purple-500 mb-6">
              <CardHeader className="pb-2">
                <CardTitle className="text-purple-700 flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  {language === 'es' ? 'Camino de Crecimiento' : 'Growth Path'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700">{employeeReport.primary_type.growth_path}</p>
              </CardContent>
            </Card>
          )}

          {/* Wing Type - Detailed */}
          {employeeReport.wing_type && (
            <Card className="border-2 mb-6" style={{ borderColor: employeeReport.wing_type.color }}>
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <span className="text-2xl">{employeeReport.wing_type.icon}</span>
                  <div>
                    <span>{language === 'es' ? 'Ala' : 'Wing'}: Tipo {employeeReport.wing_type.number} - </span>
                    <span style={{ color: employeeReport.wing_type.color }}>
                      {employeeReport.wing_type.name}
                    </span>
                  </div>
                </CardTitle>
                <CardDescription>
                  {language === 'es' 
                    ? 'El ala influye y matiza las características de tu tipo principal' 
                    : 'The wing influences and nuances your primary type characteristics'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 mb-4">{employeeReport.wing_type.description}</p>
                
                <div className="grid md:grid-cols-2 gap-4">
                  {employeeReport.wing_type.strengths?.length > 0 && (
                    <div>
                      <p className="font-medium text-green-700 mb-2">
                        {language === 'es' ? 'Aporta estas fortalezas:' : 'Contributes these strengths:'}
                      </p>
                      <ul className="space-y-1">
                        {employeeReport.wing_type.strengths.slice(0, 3).map((s, i) => (
                          <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                            <span className="text-green-500">+</span> {s}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {employeeReport.wing_type.challenges?.length > 0 && (
                    <div>
                      <p className="font-medium text-amber-700 mb-2">
                        {language === 'es' ? 'Puede intensificar:' : 'May intensify:'}
                      </p>
                      <ul className="space-y-1">
                        {employeeReport.wing_type.challenges.slice(0, 3).map((c, i) => (
                          <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                            <span className="text-amber-500">~</span> {c}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* All Types Distribution */}
          {employeeReport.all_percentages && Array.isArray(employeeReport.all_percentages) && employeeReport.all_percentages.length > 0 && (
            <Card className="mb-6">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" style={{ color: '#8B5CF6' }} />
                  {language === 'es' ? 'Distribución de Todos los Tipos' : 'All Types Distribution'}
                </CardTitle>
                <CardDescription>
                  {language === 'es' 
                    ? 'Porcentaje de afinidad con cada tipo de Eneagrama' 
                    : 'Affinity percentage with each Enneagram type'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {employeeReport.all_percentages.map((type) => (
                    <div key={type.type} className="flex items-center gap-3">
                      <span className="w-8 text-center text-xl">{ENNEAGRAM_TYPES[type.type]?.icon}</span>
                      <span className="w-20 text-sm">Tipo {type.type}</span>
                      <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all"
                          style={{ 
                            width: `${type.percentage}%`,
                            backgroundColor: ENNEAGRAM_TYPES[type.type]?.color
                          }}
                        />
                      </div>
                      <span className="w-14 text-right text-sm font-medium">{type.percentage.toFixed(1)}%</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Types Guide at the end */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2">
                <Info className="h-5 w-5" style={{ color: '#8B5CF6' }} />
                {language === 'es' ? 'Guía de los 9 Tipos de Eneagrama' : 'Guide to the 9 Enneagram Types'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(ENNEAGRAM_TYPES).map(([num, type]) => (
                  <div 
                    key={num} 
                    className={`p-2 rounded-lg border flex items-start gap-2 ${parseInt(num) === employeeReport.primary_type?.number ? 'ring-2 ring-offset-1' : ''}`}
                    style={{ 
                      borderLeftWidth: '3px', 
                      borderLeftColor: type.color,
                      ...(parseInt(num) === employeeReport.primary_type?.number ? { ringColor: type.color } : {})
                    }}
                  >
                    <span className="text-lg">{type.icon}</span>
                    <div className="min-w-0">
                      <p className="font-semibold text-xs">Tipo {num}</p>
                      <p className="text-xs" style={{ color: type.color }}>{type.name[language]}</p>
                      <p className="text-xs text-gray-500 line-clamp-2">{type.brief[language]}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Employee List for Reports
  if (showReports) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => setShowReports(false)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {language === 'es' ? 'Regresar' : 'Back'}
        </Button>

        <h2 className="text-xl font-bold">
          {language === 'es' ? 'Reportes de Eneagrama' : 'Enneagram Reports'}
        </h2>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">
              {language === 'es' ? 'Selecciona un empleado para ver su reporte' : 'Select an employee to view their report'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {employees.filter(e => e.has_enneagram).map((emp) => (
                <div 
                  key={emp.id} 
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                  onClick={() => loadEmployeeReport(emp)}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{emp.icon}</span>
                    <div>
                      <p className="font-medium">{emp.nombre}</p>
                      <p className="text-sm text-gray-500">{emp.email}</p>
                      {emp.departamento && (
                        <Badge variant="outline" className="mt-1 text-xs">{emp.departamento}</Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge style={{ backgroundColor: `${emp.color}20`, color: emp.color }}>
                      Tipo {emp.primary_type}: {emp.type_name}
                    </Badge>
                    <Eye className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
              ))}
              
              {employees.filter(e => e.has_enneagram).length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  {language === 'es' 
                    ? 'No hay empleados con evaluación completada' 
                    : 'No employees with completed assessment'}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {loadingReport && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#8B5CF6' }} />
          </div>
        )}
      </div>
    );
  }

  // Main Dashboard View
  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin mr-3" style={{ color: '#8B5CF6' }} />
          <span className="text-gray-500">
            {language === 'es' ? 'Cargando...' : 'Loading...'}
          </span>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Reports Buttons at TOP */}
      <Card className="bg-gradient-to-r from-purple-50 to-white">
        <CardContent className="py-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="font-semibold text-lg text-purple-700">
                {language === 'es' ? 'Reportes de Personalidad' : 'Personality Reports'}
              </h3>
              <p className="text-gray-600 text-sm">
                {language === 'es' 
                  ? 'Ver reportes de Eneagrama e Indicadores HR' 
                  : 'View Enneagram and HR Indicators reports'}
              </p>
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={() => setShowReports(true)}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <BarChart3 className="h-4 w-4 mr-2" />
                {language === 'es' ? 'Ver Reportes Eneagrama' : 'View Enneagram Reports'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Enneagram Dashboard */}

      {/* Key Metrics */}
      <Card className="border-l-4" style={{ borderLeftColor: '#8B5CF6' }}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center text-lg">
              <span className="text-xl mr-2">🎭</span>
              {language === 'es' ? 'Dashboard de Eneagrama' : 'Enneagram Dashboard'}
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={loadData}>
              <RefreshCw className="h-4 w-4 mr-1" />
              {language === 'es' ? 'Actualizar' : 'Refresh'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {stats?.has_data ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="text-center p-3 bg-purple-50 rounded-lg">
                  <div className="text-2xl font-bold text-purple-600">{stats.total_completed}</div>
                  <div className="text-xs text-gray-600">
                    {language === 'es' ? 'Completadas' : 'Completed'}
                  </div>
                </div>
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">{stats.total_employees}</div>
                  <div className="text-xs text-gray-600">Total</div>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">{stats.completion_rate}%</div>
                  <div className="text-xs text-gray-600">
                    {language === 'es' ? 'Tasa' : 'Rate'}
                  </div>
                </div>
                <div className="text-center p-3 rounded-lg" style={{ backgroundColor: `${stats.dominant_type?.color}15` }}>
                  <div className="text-2xl">{stats.dominant_type?.icon}</div>
                  <div className="text-xs text-gray-600">
                    {language === 'es' ? 'Dominante' : 'Dominant'}
                  </div>
                </div>
              </div>
              
              <div className="mb-2">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">
                    {language === 'es' ? 'Progreso' : 'Progress'}
                  </span>
                  <span className="font-medium">{stats.total_completed}/{stats.total_employees}</span>
                </div>
                <Progress value={stats.completion_rate} className="h-2" />
              </div>
            </>
          ) : (
            <div className="text-center py-6 text-gray-500">
              {language === 'es' 
                ? 'Aún no hay datos de Eneagrama' 
                : 'No Enneagram data yet'}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Type Distribution */}
      {stats?.has_data && stats.type_distribution && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center text-base">
              <BarChart3 className="h-5 w-5 mr-2" style={{ color: '#8B5CF6' }} />
              {language === 'es' ? 'Distribución de Tipos' : 'Type Distribution'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats.type_distribution.map((type) => (
                <div key={type.type} className="flex items-center gap-2">
                  <div className="w-6 text-center text-lg">{type.icon}</div>
                  <div className="w-16 text-sm">Tipo {type.type}</div>
                  <div className="flex-1">
                    <div className="h-5 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all flex items-center justify-end pr-2"
                        style={{ 
                          width: `${Math.max(type.percentage, 5)}%`,
                          backgroundColor: type.color,
                          minWidth: type.count > 0 ? '30px' : '0'
                        }}
                      >
                        {type.count > 0 && (
                          <span className="text-xs font-medium text-white">{type.count}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="w-12 text-right text-sm font-medium" style={{ color: type.color }}>
                    {type.percentage}%
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Insights */}
      {stats?.insights && stats.insights.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center text-base">
              <TrendingUp className="h-5 w-5 mr-2" style={{ color: '#8B5CF6' }} />
              Insights
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-3">
              {stats.insights.map((insight, idx) => (
                <div key={idx} className="p-3 rounded-lg border bg-gray-50">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">{insight.icon}</span>
                    <h4 className="font-semibold text-sm">{insight[`title_${language}`]}</h4>
                  </div>
                  <p className="text-xs text-gray-600">{insight[`description_${language}`]}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Employees List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center">
              <Users className="h-5 w-5 mr-2" style={{ color: '#8B5CF6' }} />
              {language === 'es' ? 'Empleados' : 'Employees'}
            </span>
            <Badge variant="secondary">
              {employees.filter(e => e.has_enneagram).length}/{employees.length}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {employees.map((emp) => (
              <div key={emp.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  {emp.has_enneagram && <span className="text-lg">{emp.icon}</span>}
                  <div>
                    <p className="font-medium text-sm">{emp.nombre}</p>
                    <p className="text-xs text-gray-500">{emp.email}</p>
                  </div>
                </div>
                <div>
                  {emp.has_enneagram ? (
                    <Badge style={{ backgroundColor: `${emp.color}20`, color: emp.color }} className="text-xs">
                      Tipo {emp.primary_type}
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="text-xs">
                      <Clock className="h-3 w-3 mr-1" />
                      {language === 'es' ? 'Pendiente' : 'Pending'}
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Compact Legend */}
      <Card>
        <CardHeader className="pb-2">
          <div 
            className="flex items-center justify-between cursor-pointer"
            onClick={() => setShowLegend(!showLegend)}
          >
            <CardTitle className="flex items-center text-base">
              <Info className="h-5 w-5 mr-2" style={{ color: '#8B5CF6' }} />
              {language === 'es' ? 'Guía de Tipos' : 'Types Guide'}
            </CardTitle>
            {showLegend ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </div>
        </CardHeader>
        {showLegend && (
          <CardContent>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(ENNEAGRAM_TYPES).map(([num, type]) => (
                <div 
                  key={num} 
                  className="p-2 rounded-lg border flex items-start gap-2"
                  style={{ borderLeftWidth: '3px', borderLeftColor: type.color }}
                >
                  <span className="text-lg">{type.icon}</span>
                  <div className="min-w-0">
                    <p className="font-semibold text-xs">Tipo {num}</p>
                    <p className="text-xs" style={{ color: type.color }}>{type.name[language]}</p>
                    <p className="text-xs text-gray-500 truncate">{type.brief[language]}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default PersonalityTab;
